import { useState, useEffect, type ReactNode } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuery, useMutation } from '@tanstack/react-query';
import { Loader2, Copy, FilePlus, Check } from 'lucide-react';
import toast from 'react-hot-toast';
import Modal from '@/components/ui/Modal';
import certificateTemplateService from '@/services/certificate-template.service';
import { ADMIN_CERTIFICATE_TEMPLATE_CREATE } from '@/constants/constant-routes';
import {
  CertificateTemplateType,
  CERTIFICATE_TEMPLATE_TYPE_LABELS,
  CERTIFICATE_TEMPLATE_TYPE_DESCRIPTIONS
} from '@/types/certificate-template.types';

interface CreateTemplateModalProps {
  isOpen: boolean;
  onClose: () => void;
}

/** Passed to the editor's create mode in router state (nothing is in the database yet) */
export interface NewTemplateDraft {
  template_type: CertificateTemplateType;
  template_name: string;
  source_template_id?: string; // copy this template's design; undefined = blank canvas
}

type StartFrom = 'blank' | 'current';

const TYPE_DOTS: Record<CertificateTemplateType, string> = {
  [CertificateTemplateType.COURSE_COMPLETION]: 'bg-blue-500',
  [CertificateTemplateType.LEARNING_PATH_COMPLETION]: 'bg-purple-500',
  [CertificateTemplateType.CERTIFICATION_GOLD]: 'bg-amber-400',
  [CertificateTemplateType.CERTIFICATION_SILVER]: 'bg-gray-400',
  [CertificateTemplateType.CERTIFICATION_BRONZE]: 'bg-orange-700'
};

/** Create Template: pick type, name and starting design, then open the editor (saved only on Save). */
export default function CreateTemplateModal({ isOpen, onClose }: CreateTemplateModalProps) {
  const navigate = useNavigate();
  const [templateType, setTemplateType] = useState<CertificateTemplateType | null>(null);
  const [templateName, setTemplateName] = useState('');
  const [startFrom, setStartFrom] = useState<StartFrom>('blank');
  const [nameError, setNameError] = useState('');

  // Fresh form every time the modal opens
  useEffect(() => {
    if (!isOpen) return;
    setTemplateType(null);
    setTemplateName('');
    setStartFrom('blank');
    setNameError('');
  }, [isOpen]);

  // Template currently used for issuing this type (null for a tenant with none yet)
  const { data: currentTemplate, isLoading: isLoadingCurrent, isError: currentFailed } = useQuery({
    queryKey: ['certificateTemplates', 'in-use', templateType],
    queryFn: () => certificateTemplateService.getTemplateByType(templateType!),
    enabled: isOpen && !!templateType
  });
  const canCopyCurrent = !!currentTemplate?.html_template;

  // Nothing to copy for this type → fall back to the blank canvas
  useEffect(() => {
    if (templateType && !isLoadingCurrent && !canCopyCurrent) setStartFrom('blank');
  }, [templateType, isLoadingCurrent, canCopyCurrent]);

  // Nothing is saved here — the editor creates the template on its first Save
  const openEditorMutation = useMutation({
    mutationFn: () => certificateTemplateService.isTemplateNameTaken(templateName),
    onSuccess: (taken) => {
      if (taken) {
        setNameError('A template with this name already exists. Please use a different name.');
        return;
      }
      const newTemplate: NewTemplateDraft = {
        template_type: templateType!,
        template_name: templateName.trim(),
        source_template_id: startFrom === 'current' ? currentTemplate?._id : undefined
      };
      onClose();
      navigate(ADMIN_CERTIFICATE_TEMPLATE_CREATE, { state: { newTemplate } });
    },
    onError: (err: Error) => {
      toast.error(err.message || 'Could not check the template name');
    }
  });

  const isSaving = openEditorMutation.isPending;

  const handleSelectType = (type: CertificateTemplateType) => {
    setTemplateType(type);
    setNameError('');
  };

  const handleCreate = () => {
    if (!templateType) return;
    const name = templateName.trim();
    if (!name) {
      setNameError('Template name is required.');
      return;
    }
    if (name.length < 3) {
      setNameError('Template name must be at least 3 characters.');
      return;
    }
    if (name.length > 100) {
      setNameError('Template name must be 100 characters or less.');
      return;
    }
    openEditorMutation.mutate();
  };

  const handleClose = () => { if (!isSaving) onClose(); };

  const footer = (
    <div className="flex items-center justify-end gap-3">
      <button
        type="button"
        onClick={handleClose}
        disabled={isSaving}
        className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
      >
        Cancel
      </button>
      <button
        type="button"
        onClick={handleCreate}
        disabled={!templateType || isSaving || isLoadingCurrent}
        className="px-4 py-2 text-sm font-semibold text-white bg-primary-600 rounded-lg hover:bg-primary-700 disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
      >
        {isSaving && <Loader2 className="w-4 h-4 animate-spin" />}
        Create &amp; Open Editor
      </button>
    </div>
  );

  const startOption = (value: StartFrom, title: string, description: string, icon: ReactNode, disabled: boolean) => (
    <button
      type="button"
      onClick={() => !disabled && setStartFrom(value)}
      aria-pressed={startFrom === value}
      disabled={disabled || isSaving}
      className={`w-full text-left p-3 rounded-lg border-2 flex items-start gap-3 transition-colors disabled:opacity-50 disabled:cursor-not-allowed ${
        startFrom === value ? 'border-primary-500 bg-primary-50' : 'border-gray-200 hover:border-gray-300'
      }`}
    >
      <div className="mt-0.5 text-gray-600">{icon}</div>
      <div className="flex-1 min-w-0">
        <p className="text-sm font-semibold text-gray-900">{title}</p>
        <p className="text-xs text-gray-500 mt-0.5">{description}</p>
      </div>
      {startFrom === value && <Check className="w-4 h-4 text-primary-600 flex-shrink-0 mt-0.5" />}
    </button>
  );

  return (
    <Modal
      isOpen={isOpen}
      onClose={handleClose}
      title="Create Certificate Template"
      subtitle="Nothing is saved until you click Save in the editor. Learners keep getting the current design until you choose to use the new one."
      size="lg"
      footer={footer}
      closeOnOverlayClick={!isSaving}
      maxHeight="90vh"
    >
      <div className="space-y-5">
        {/* 1. Type */}
        <div>
          <p className="text-sm font-medium text-gray-900 mb-2">1. Which certificate is this for?</p>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
            {Object.values(CertificateTemplateType).map(type => (
              <button
                key={type}
                type="button"
                onClick={() => handleSelectType(type)}
                aria-pressed={templateType === type}
                disabled={isSaving}
                className={`text-left p-3 rounded-lg border-2 transition-colors disabled:cursor-not-allowed ${
                  templateType === type ? 'border-primary-500 bg-primary-50' : 'border-gray-200 hover:border-gray-300'
                }`}
              >
                <div className="flex items-center gap-2">
                  <span className={`w-2.5 h-2.5 rounded-full ${TYPE_DOTS[type]}`} />
                  <span className="text-sm font-semibold text-gray-900">{CERTIFICATE_TEMPLATE_TYPE_LABELS[type]}</span>
                </div>
                <p className="text-xs text-gray-500 mt-1">{CERTIFICATE_TEMPLATE_TYPE_DESCRIPTIONS[type]}</p>
              </button>
            ))}
          </div>
        </div>

        {templateType && (
          <>
            {/* 2. Name */}
            <div>
              <label htmlFor="new-template-name" className="block text-sm font-medium text-gray-900 mb-2">
                2. Template name <span className="text-red-500">*</span>
              </label>
              <input
                id="new-template-name"
                type="text"
                value={templateName}
                maxLength={100}
                disabled={isSaving}
                onChange={e => { setTemplateName(e.target.value); setNameError(''); }}
                className={`w-full px-3 py-2 text-sm border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 ${nameError ? 'border-red-400' : 'border-gray-300'}`}
                placeholder="Enter a template name, e.g. Gold Certificate 2026"
                aria-required="true"
                aria-invalid={!!nameError}
              />
              {nameError && <p className="text-xs text-red-600 mt-1">{nameError}</p>}
            </div>

            {/* 3. Starting design */}
            <div>
              <p className="text-sm font-medium text-gray-900 mb-2">3. Start from</p>
              {isLoadingCurrent ? (
                <div className="flex items-center text-sm text-gray-500 py-3">
                  <Loader2 className="w-4 h-4 animate-spin mr-2" /> Checking the current design...
                </div>
              ) : (
                <div className="space-y-2">
                  {startOption(
                    'blank',
                    'Blank canvas',
                    'Start from an empty page and add your own title, fields, QR code and signature.',
                    <FilePlus className="w-4 h-4" />,
                    false
                  )}
                  {startOption(
                    'current',
                    'Copy of current design',
                    canCopyCurrent
                      ? `Starts as an exact copy of "${currentTemplate!.template_name}". Best for small changes.`
                      : currentFailed
                        ? 'Could not load the current design.'
                        : 'No default template for this type yet.',
                    <Copy className="w-4 h-4" />,
                    !canCopyCurrent
                  )}
                </div>
              )}
            </div>
          </>
        )}
      </div>
    </Modal>
  );
}
